import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Phone, User, Clock, Check, X, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsManagement({ getAuthHeaders }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads?status=${filter}`, { headers: getAuthHeaders() });
      setLeads(res.data.leads || []);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status }, { headers: getAuthHeaders() });
      toast.success(`Lead marked as ${status}`);
      fetchLeads();
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-yellow-100 text-yellow-700',
    converted: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="space-y-4" data-testid="leads-management">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#FF3008]" />
              Lead Management ({leads.length})
            </CardTitle>
            <div className="flex gap-2">
              {['all', 'new', 'contacted', 'converted', 'closed'].map(s => (
                <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'}
                  onClick={() => setFilter(s)}
                  className={filter === s ? 'bg-[#FF3008] hover:bg-[#E02800]' : ''}
                  data-testid={`filter-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={fetchLeads} data-testid="refresh-leads">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No leads yet. They'll appear here as visitors submit the form.</p>
            </div>
          ) : (
            <div className="divide-y">
              {leads.map(lead => (
                <div key={lead.lead_id} className="py-4 flex items-start justify-between gap-4" data-testid={`lead-${lead.lead_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900 truncate">{lead.name || 'No name'}</p>
                      <Badge className={statusColors[lead.status] || statusColors.new}>{lead.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>
                      {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                      {lead.service_interest && <span className="flex items-center gap-1"><User className="w-3 h-3" />{lead.service_interest}</span>}
                    </div>
                    {lead.message && <p className="text-sm text-gray-500 mt-1">{lead.message}</p>}
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(lead.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {lead.status === 'new' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(lead.lead_id, 'contacted')} data-testid={`contact-${lead.lead_id}`}>
                        <Phone className="w-3 h-3 mr-1" /> Contacted
                      </Button>
                    )}
                    {(lead.status === 'new' || lead.status === 'contacted') && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateStatus(lead.lead_id, 'converted')} data-testid={`convert-${lead.lead_id}`}>
                        <Check className="w-3 h-3 mr-1" /> Converted
                      </Button>
                    )}
                    {lead.status !== 'closed' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(lead.lead_id, 'closed')} data-testid={`close-${lead.lead_id}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
